import { createSlice } from "@reduxjs/toolkit";
import {
  fetchSavedProducts,
  toggleProductSaved,
  fetchLikedProducts,
  toggleProductLiked,
} from "../thunks/bookmarks.thunk";
import { BookmarksState, SavedProduct } from "../interfaces/bookmarks.interface";

const initialState: BookmarksState = {
  savedProductIds: [],
  savedProducts: [],
  savedProductsPagination: {
    total: 0,
    page: 1,
    limit: 15,
    totalPages: 1,
  },
  isLoadingSavedProducts: false,
  isLoadingMoreSavedProducts: false,
  likedProductIds: [],
  likedProducts: [],
  likedProductsPagination: {
    total: 0,
    page: 1,
    limit: 15,
    totalPages: 1,
  },
  isLoadingLikedProducts: false,
  isLoadingMoreLikedProducts: false,
  loading: false,
  error: null,
};

const bookmarksSlice = createSlice({
  name: "bookmarks",
  initialState,
  reducers: {
    clearBookmarksError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const extractId = (p: any): string | null => {
      const id = p?.id ?? p?._id ?? p?.productId;
      return id ? String(id) : null;
    };

    builder
      // Fetch saved products
      .addCase(fetchSavedProducts.pending, (state, action) => {
        const append = !!action.meta.arg?.append;
        if (append) {
          state.isLoadingMoreSavedProducts = true;
        } else {
          state.isLoadingSavedProducts = true;
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSavedProducts.fulfilled, (state, action) => {
        state.isLoadingSavedProducts = false;
        state.isLoadingMoreSavedProducts = false;
        state.loading = false;
        const { products: incoming, pagination, append } = action.payload;
        state.savedProductsPagination = pagination;

        // Extract IDs from incoming products
        const ids = incoming.map(extractId).filter(Boolean) as string[];

        if ((incoming.length === 0 || ids.length === 0) && state.savedProductIds.length > 0 && !append) {
          return;
        }

        if (append) {
          const existingIds = new Set(
            state.savedProducts.map((p: SavedProduct) => String(p.id)),
          );
          const unique = incoming.filter(
            (p: SavedProduct) => !existingIds.has(String(p.id)),
          );
          state.savedProducts.push(...unique);
        } else {
          state.savedProducts = ids.length > 0
            ? incoming.filter((p: SavedProduct) => !!extractId(p))
            : [];
        }
        state.savedProductIds = append
          ? Array.from(new Set([...state.savedProductIds, ...ids]))
          : ids;
      })
      .addCase(fetchSavedProducts.rejected, (state, action) => {
        state.isLoadingSavedProducts = false;
        state.isLoadingMoreSavedProducts = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle product saved
      .addCase(toggleProductSaved.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProductSaved.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, action: toggleAction, product } = action.payload as any;

        if (toggleAction === "save") {
          if (!state.savedProductIds.includes(productId)) {
            state.savedProductIds.push(productId);
          }
          if (product) {
            const exists = state.savedProducts.some(
              (p: any) => String((p as any)?.id || (p as any)?.productId) === String(productId),
            );
            if (!exists) {
              state.savedProducts.unshift(product);
            }
          }
        } else {
          state.savedProductIds = state.savedProductIds.filter(
            (id) => id !== productId,
          );
          state.savedProducts = state.savedProducts.filter(
            (p: any) => String(p?.id || p?.productId) !== String(productId),
          );
          // If user removed the last bookmark, ensure list view resets cleanly.
          if (state.savedProductIds.length === 0) {
            state.savedProducts = [];
          }
        }
      })
      .addCase(toggleProductSaved.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch liked products
      .addCase(fetchLikedProducts.pending, (state, action) => {
        const append = !!action.meta.arg?.append;
        if (append) {
          state.isLoadingMoreLikedProducts = true;
        } else {
          state.isLoadingLikedProducts = true;
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchLikedProducts.fulfilled, (state, action) => {
        state.isLoadingLikedProducts = false;
        state.isLoadingMoreLikedProducts = false;
        state.loading = false;
        const { products: incoming, pagination, append } = action.payload;
        state.likedProductsPagination = pagination;
        if (append) {
          const existingIds = new Set(
            state.likedProducts.map((p: SavedProduct) => String(p.id)),
          );
          const unique = incoming.filter(
            (p: SavedProduct) => !existingIds.has(String(p.id)),
          );
          state.likedProducts.push(...unique);
        } else {
          state.likedProducts = incoming;
        }
        state.likedProductIds = state.likedProducts.map((p: SavedProduct) =>
          String(p.id || (p as any).productId),
        );
      })
      .addCase(fetchLikedProducts.rejected, (state, action) => {
        state.isLoadingLikedProducts = false;
        state.isLoadingMoreLikedProducts = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle product liked
      .addCase(toggleProductLiked.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProductLiked.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, action: toggleAction } = action.payload;

        if (toggleAction === "like") {
          if (!state.likedProductIds.includes(productId)) {
            state.likedProductIds.push(productId);
          }
        } else {
          state.likedProductIds = state.likedProductIds.filter(
            (id) => id !== productId,
          );
        }
      })
      .addCase(toggleProductLiked.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearBookmarksError } = bookmarksSlice.actions;
export default bookmarksSlice.reducer;
